import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import * as Const from '../Const';
import {
  useGetPlaceFacePicturesQuery,
  useGetPlaceFaceQuery,
} from '../generated/graphql';
import { Loading } from '../Loading';
import { Button } from '../components/Button';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { EmptyRow } from '../components/EmptyRow';
import { SectionHeader } from '../components/SectionHeader';
import { formatDate } from '../utils/DateHelper';
import './PlaceDxfPicturesPage.css';
import path from 'path';
import { TableScroll } from '../components/TableScroll';
import { FilePreviewWindow } from '../components/FilePreviewWindow';

type PlaceDxfPicturesPage = RouteComponentProps<{
  placeId: string;
  placeFaceId: string;
}>;

/** 表の列数。0件の行を表の幅いっぱいに広げるために使う */
const COLUMN_COUNT = 5;

/**
 * 写真の表示用のURLを返す。
 * 拡張子のない写真は、サーバーが画像の種類を判断できるよう「.jpg」と「noext」を付けて取得する。
 */
function getPictureViewUrl(url: string): string {
  if (path.extname(url) === '') {
    return Const.API_URL + url + '.jpg?noext=true';
  }
  return Const.API_URL + url;
}

/** 写真をダウンロードさせるためのURLを返す */
function getPictureDownloadUrl(url: string): string {
  if (path.extname(url) === '') {
    return Const.API_URL + url + '.jpg?noext=true&download';
  }
  return Const.API_URL + url + '?download';
}

/** 写真を保存する。一覧の行の［ダウンロード］と、拡大表示の窓の［ダウンロード］で使う */
function downloadPicture(url: string): void {
  window.location.href = getPictureDownloadUrl(url);
}

interface ViewingPicture {
  name: string;
  full_url: string;
}

export const PlaceDxfPicturesPage: React.FC<PlaceDxfPicturesPage> = function (
  props
) {
  const placeId = parseInt(props.match.params.placeId);
  const placeFaceId = parseInt(props.match.params.placeFaceId);
  const [viewingPicture, setViewingPicture] = React.useState<ViewingPicture>();

  const getPictures = useGetPlaceFacePicturesQuery({
    variables: { placeFaceId },
  });
  // 見出しに出す図面名を取得する。
  // 写真のクエリは同じ図面をIDなしで取得しており、キャッシュに書くと互いの内容を上書きし合うため、キャッシュを使わない
  const getFaceResult = useGetPlaceFaceQuery({
    variables: { placeFaceId },
    fetchPolicy: 'no-cache',
  });

  if (getPictures.error) {
    return <GraphQLErrorDialog error={getPictures.error} />;
  }

  if (getFaceResult.error) {
    return <GraphQLErrorDialog error={getFaceResult.error} />;
  }

  if (getPictures.loading || !getPictures.data || !getFaceResult.data) {
    return <Loading />;
  }

  const faceName = getFaceResult.data.getPlaceFace.dxf_file_name;
  const data = getPictures.data;
  const pictureIdToMarkerMap: { [key: number]: { index: number, deleted: boolean } } = {};
  for (const marker of data.getPlaceFace.markers) {
    pictureIdToMarkerMap[marker.picture_id] = marker;
  }

  const markerPictures = [];
  const noMarkerPictures = [];
  const deletedPictures = [];

  for (const picture of data.getPlaceFace.pictures) {
    const marker = pictureIdToMarkerMap[picture.id];
    if (marker) {
      if (marker.deleted) {
        deletedPictures.push(picture);
      } else {
        markerPictures.push(picture);
      }
    } else {
      noMarkerPictures.push(picture);
    }
  }

  const pictures = [
    ...markerPictures.sort((a, b) => {
      const m1 = pictureIdToMarkerMap[a.id];
      const m2 = pictureIdToMarkerMap[b.id];
      return m1.index - m2.index;
    }),
    ...noMarkerPictures,
    ...deletedPictures,
  ];

  let rows;
  if (pictures.length === 0) {
    rows = <EmptyRow colSpan={COLUMN_COUNT} message="写真はありません" />;
  } else {
    rows = pictures.map((picture) => {
      return (
        <tr key={picture.id}>
          <td>{(function () {
            const marker = pictureIdToMarkerMap[picture.id];
            if (!marker) {
              return '-';
            }
            if (marker.deleted) {
              return '削除済み';
            }
            return marker.index;
          })()}</td>
          <td>{picture.name}</td>
          <td>
            <button
              type="button"
              className="PlaceDxfPicturesPage-thumbnail_button"
              aria-label={`${picture.name}を拡大表示`}
              onClick={() => setViewingPicture(picture)}
            >
              <img
                className="PlaceDxfPicturesPage-thumbnail"
                src={Const.API_URL + picture.thumbnail_url}
                alt={picture.name}
              />
            </button>
          </td>
          <td>{formatDate(picture.created)}</td>
          <td className="App-table_button_cell">
            <div className="App-table_actions">
              <Button
                mini={true}
                color="white"
                onClick={() => downloadPicture(picture.full_url)}
              >
                ダウンロード
              </Button>
            </div>
          </td>
        </tr>
      );
    });
  }

  let pictureModal;
  if (viewingPicture) {
    pictureModal = (
      <FilePreviewWindow
        fileName={viewingPicture.name}
        src={getPictureViewUrl(viewingPicture.full_url)}
        onClose={() => setViewingPicture(undefined)}
        onDownload={() => downloadPicture(viewingPicture.full_url)}
      />
    );
  }

  return (
    <div className="PlaceDxfPicturesPage">
      {pictureModal}
      <Link className="PlaceDxfPicturesPage-back_link" to={`/places/${placeId}/faces`}>
        ← 図面一覧
      </Link>
      <SectionHeader title={`${faceName}の写真`} />
      <p className="App-table_count">{pictures.length} 件</p>
      <TableScroll storageKey="placeFacePictures">
        <table className="App-table">
          <thead>
            <tr>
              <th>番号</th>
              <th>名前</th>
              <th>画像</th>
              <th>作成日</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </TableScroll>
    </div>
  );
};
